import { CSSObject } from '@emotion/react'
import { useState } from 'react'
import { Button, kuiperClassNames } from '@opallabs/kuiper'
import { doNothing } from '../Content/helpers/functions/doNothing'
import { type UploadType } from '../Content/helpers/types'
import { styles } from './styles'

interface Props {
  allowedUploadTypes: UploadType
  borderRadius?: string
  disableDrop?: boolean
  onDragDrop: (e: React.DragEvent<HTMLDivElement>) => void
  onShowAssetPicker?: () => void
  postType?: string
  readOnly: boolean
  showButtonGroup?: boolean
  style: CSSObject
  uploadType?: 'image' | 'video'
}

const mixedTypeLabel = 'image or 1 video'

export const SingleAssetPicker: React.FC<Props> = ({
  allowedUploadTypes,
  borderRadius,
  disableDrop,
  onDragDrop,
  onShowAssetPicker,
  postType,
  readOnly,
  showButtonGroup = true,
  style,
  uploadType,
}) => {
  const [dragging, setIsDragging] = useState(false)

  const dropLabel = isNaN(+allowedUploadTypes)
    ? `Drag and drop 1 ${
        allowedUploadTypes === 'mixed' ? mixedTypeLabel : allowedUploadTypes
      }`
    : `Drag and drop up to ${allowedUploadTypes} ${
        uploadType ? `${uploadType}s` : 'images or videos'
      }`

  // Remove postType check when we add drag/drop uploading images
  // to React modular content types
  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (postType && postType === 'moment') return
    e.preventDefault()
    setIsDragging(true)
  }

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (postType && postType === 'moment') return
    e.preventDefault()
    setIsDragging(false)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (postType && postType === 'moment') return
    setIsDragging(false)
    onDragDrop(e)
  }

  return (
    <div
      onDrop={disableDrop ? doNothing : onDrop}
      onDragEnter={disableDrop ? doNothing : onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={disableDrop ? doNothing : (e) => e.preventDefault()}
      className={kuiperClassNames('single-asset-picker')}
      css={styles.getAssetPickerWrapper({ style, dragging, borderRadius })}
    >
      {dragging && showButtonGroup && <span>{dropLabel}</span>}
      {!readOnly && !dragging && (
        <div
          css={styles.assetPickerBtnWrapper}
          className={kuiperClassNames('asset-picker-button-grp')}
        >
          <div css={styles.assetPickerBtnGroup}>
            {showButtonGroup && (
              <Button
                aria-hidden={true}
                className={kuiperClassNames('asset-picker-plus')}
                icon="plus"
                iconOptions={{
                  height: 10,
                  width: 10,
                }}
                onClick={onShowAssetPicker ? onShowAssetPicker : doNothing}
                style={styles.assetPickerBtnPlus}
                type="nobutton"
              />
            )}
            <Button
              ariaLabel="Select Asset from Asset Library"
              onClick={onShowAssetPicker ? onShowAssetPicker : doNothing}
              style={styles.assetPickerBtnAssetLibrary}
              type="nobutton"
              className={kuiperClassNames('asset-picker-asset-library')}
              icon="asset-library"
            />
          </div>
        </div>
      )}
    </div>
  )
}
